import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Check, X, Sparkles } from 'lucide-react';
import Header from '../components/Header';

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const tiers = [
    {
      name: 'Anonymous',
      price: 'Free',
      limit: '5 per day',
      features: [
        { text: '5 test case generations per day', included: true },
        { text: 'Azure DevOps & JIRA support', included: true },
        { text: 'No account required', included: true },
        { text: 'Ads supported', included: false }
      ]
    },
    {
      name: 'Authenticated',
      price: 'Free',
      limit: '10 per day',
      features: [
        { text: '10 test case generations per day', included: true },
        { text: 'Azure DevOps & JIRA support', included: true },
        { text: 'Free account', included: true },
        { text: 'Ads supported', included: false }
      ],
      recommended: true
    }
    // Paid Subscriber tier hidden for now
    /* ,
    {
      name: 'Paid Subscriber',
      price: '$4.50',
      period: '/month',
      limit: 'Unlimited',
      features: [
        { text: 'Unlimited test case generations', included: true },
        { text: 'Azure DevOps & JIRA support', included: true },
        { text: 'Ad-free experience', included: true },
        { text: 'Priority support', included: true },
        { text: 'Early access to new features', included: true }
      ],
      recommended: true
    }
    */
  ];

  const handleUpgrade = () => {
    if (!user) {
      navigate('/signup');
    }
    // Paid subscription functionality hidden for now
    // else {
    //   navigate('/profile');
    // }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-6">Simple, Transparent Pricing</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your needs. Upgrade or cancel anytime.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">{tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-card border rounded-lg p-8 ${
                  tier.recommended
                    ? 'border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.2)]'
                    : 'border-border hover:border-border/80'
                }`}
                data-testid={`pricing-tier-${tier.name.toLowerCase().replace(' ', '-')}`}
              >
                {tier.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-violet-600 text-white text-sm font-medium px-4 py-1 rounded-full flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Recommended
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-heading font-bold mb-2">{tier.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">{tier.price}</span>
                    {tier.period && <span className="text-muted-foreground">{tier.period}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{tier.limit} generations</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-foreground' : 'text-muted-foreground'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Upgrade button hidden for now */}
                {/* {tier.recommended && (
                  <Button
                    onClick={handleUpgrade}
                    className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90"
                    data-testid="upgrade-button"
                  >
                    {user ? 'Upgrade Now' : 'Get Started'}
                  </Button>
                )} */}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-16 text-center"
          >
            <p className="text-muted-foreground">
              All plans include access to both Azure DevOps and JIRA export formats.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}